import Document, { Html, Head, Main, NextScript } from 'next/document'
import Script from 'next/script'

import environment from 'environment'

import { getThemeName, THEME_CLASS_NAMES } from 'components/misc/GlobalToolbar/ThemeDropdown'
import { ROLES } from 'types/user'

//@ts-ignore
export default class MyDocument extends Document {
  static async getInitialProps(ctx: any) {
    handleSetupCanary(ctx)

    const theme = getThemeName(ctx.req.cookies.tbTheme)
    const bodyClass = THEME_CLASS_NAMES[theme]

    const tier = ctx.req.cookies.tbTier || 0
    const roles = ctx.req.cookies.tbR || []
    const expiresAt = new Date(ctx.req.cookies.tbFreeTrialExpiresAt || '1999-01-01')
    const now = new Date()

    const adFree =
      roles.includes(ROLES.AD_FREE) ||
      Number(tier) ||
      (environment.nodeEnv === 'development' && !environment.enableAdsOnLocalhost) ||
      now <= expiresAt

    try {
      const initialProps = await Document.getInitialProps(ctx)

      return {
        ...initialProps,
        bodyClass,
        adFree,
        styles: <>{initialProps.styles}</>,
      }
    } catch (err) {
      console.error(err)

      return {
        bodyClass,
        adFree,
      }
    }
  }

  render() {
    return (
      <Html lang="en">
        <Head>
          <meta property="og:site_name" content="Archidekt" />

          <link
            href="https://cdn.jsdelivr.net/npm/keyrune@latest/css/keyrune.css"
            rel="stylesheet"
            type="text/css"
            crossOrigin="anonymous"
          />
          {/* Required for the rich text toolbar - could be removed if we made a custom one ourselves for desktop */}
          <link rel="stylesheet" href="/quill-snow-1.2.6.css" />
          <link
            href="//cdn.jsdelivr.net/npm/mana-font@latest/css/mana.css"
            rel="stylesheet"
            type="text/css"
            crossOrigin="anonymous"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Lato:ital,wght@0,100;0,300;0,400;0,700;0,900;1,100;1,300;1,400;1,700;1,900&display=swap"
            rel="stylesheet"
          />

          {/* Smooth scrolling ios - https://github.com/iamdustan/smoothscroll - self-hosted, must run before hydration */}
          <Script src="/smoothscroll-polyfill-0.4.4.min.js" strategy="beforeInteractive" />

          {/* Ad shit */}
          {/* @ts-ignore */}
          {!this.props.adFree && (
            <script type="module" src="https://ads.archidekt.com/loader.js" crossOrigin="use-credentials" />
          )}

          {/* preconnect to resources we're very likely going to need */}
          {/* no more than 6. look at "Connection View" in WebPageTest */}
          {/* fonts.gstatic.com preconnect automatically added by Next.js */}
          <link href="https://cdn.jsdelivr.net" rel="preconnect" />
          <link href="https://cdn.archidekt.com" rel="preconnect" />
          <link href="https://storage.googleapis.com" rel="preconnect" />
          <link href="https://card-images.archidekt.com" rel="preconnect" />
          {/* @ts-ignore */}
          {!this.props.adFree && (
            <link href="https://ads.archidekt.com" rel="preconnect" crossOrigin="use-credentials" />
          )}
          <link href="https://www.googletagmanager.com" rel="preconnect" />

          {/* dns-prefetch the same things as a fallback */}
          {/* "Implementing dns-prefetch fallback in the same <link> tag causes a bug in Safari where preconnect gets cancelled." */}
          <link href="https://cdn.jsdelivr.net" rel="dns-prefetch" />
          <link href="https://cdn.archidekt.com" rel="dns-prefetch" />
          <link href="https://storage.googleapis.com" rel="dns-prefetch" />
          <link href="https://card-images.archidekt.com" rel="dns-prefetch" />
          {/* @ts-ignore */}
          {!this.props.adFree && (
            <link href="https://ads.archidekt.com" rel="dns-prefetch" />
          )}
          <link href="https://www.googletagmanager.com" rel="dns-prefetch" />

          {/* dns-prefetch less critical stuff we might need */}
          <link href="https://cards.scryfall.io" rel="dns-prefetch" />
          <link href="https://svgs.scryfall.io" rel="dns-prefetch" />
          {/* @ts-ignore */}
          {!this.props.adFree && (
            <>
              <link href="https://securepubads.g.doubleclick.net" rel="dns-prefetch" />
              <link href="https://cdn.confiant-integrations.net" rel="dns-prefetch" />
              <link href="https://ads.pubmatic.com" rel="dns-prefetch" />
              <link href="https://prebid.spacecowmedia.com/" rel="dns-prefetch" />
            </>
          )}

        </Head>

        {/* @ts-ignore */}
        <body className={`${this.props.bodyClass} ${!this.props.adFree ? 'ads-enabled' : ''}`}>
          <Main />
          <NextScript />
        </body>
      </Html>
    )
  }
}

export const canaryCookieKey = 'canaryEnabled'
export const canaryVersionKey = 'canaryVersion'
export const canaryRandomValueKey = 'canaryRoll'

const currentCanaryVersion = '4' // Change this if we're running a new canary campagin - makes everything reroll from scratch when changed
const rolloutPercentage = 0 // Change this to set the % rollout per canary version

const handleSetupCanary = (ctx: any) => {
  const manualCanary = ctx.query.canary // Allows the user to manually override any canary property via query params
  const canaryVersion = ctx.req.cookies[canaryVersionKey] || '0'
  const canaryWasEnabled = (ctx.req.cookies[canaryCookieKey] || '0') === '1'

  const onCurrentVersion = canaryVersion === currentCanaryVersion
  const expiresAt = new Date(Date.now() + 1000 * 60 * 60 * 24 * 365) // One year

  // If the user manually switches canary versions, let them. Overrides all other factors
  if (manualCanary !== undefined && (manualCanary === '0' || manualCanary === '1')) {
    ctx.res.appendHeader('Set-Cookie', `${canaryCookieKey}=${manualCanary}; Path=/; Expires=${expiresAt}`)

    return manualCanary === '1'
  }

  // If they were ever on canary leave them on canaray. Kicking people back and forth is not a good experience.
  // If you wish to clear users off of the existing canary build, update the canaryVersionKey and start a new canary campaign from scratch
  if (onCurrentVersion && canaryWasEnabled) return true

  // If we're on the current version, use the random value stored in the cookie. If the version changes, generate a new one
  const canaryRoll = Number(onCurrentVersion ? ctx.req.cookies[canaryRandomValueKey] || Math.random() : Math.random())
  const isCanaryEnabled = canaryRoll < rolloutPercentage ? '1' : '0'

  ctx.res.appendHeader('Set-Cookie', `${canaryCookieKey}=${isCanaryEnabled}; Path=/; Expires=${expiresAt}`)
  ctx.res.appendHeader('Set-Cookie', `${canaryVersionKey}=${currentCanaryVersion}; Path=/; Expires=${expiresAt}`)
  ctx.res.appendHeader('Set-Cookie', `${canaryRandomValueKey}=${canaryRoll}; Path=/; Expires=${expiresAt}`)

  return isCanaryEnabled === '1'
}
